import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import {
  getPermittedRoutes,
  RouteConfig,
  routesConfig,
} from "../config/routes";
import { httpService } from "../services/httpService";
import { useAuth } from "../hooks";
import { IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { PERMISSION_MAPPER } from "../shared/constants";

const RouteInspector: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedRoute, setHasCheckedRoute] = useState(false);

  // Helper function to match routes exactly, including dynamic segments
  const matchRoute = (pathname: string, routePath: string): boolean => {
    // Split both paths into segments
    const pathSegments = pathname.split("/").filter(Boolean);
    const routeSegments = routePath.split("/").filter(Boolean);

    // If the number of segments don't match, it's not a match
    if (pathSegments.length !== routeSegments.length) {
      return false;
    }

    // Check each segment
    return routeSegments.every((segment, index) => {
      // If the route segment starts with ':', it's a dynamic parameter
      if (segment.startsWith(":")) {
        return true; // Any value is valid for dynamic parameters
      }
      // Otherwise, the segments must match exactly
      return segment === pathSegments[index];
    });
  };

  useEffect(() => {
    if (isLoading) return;

    const accessToken = httpService.getAccessToken();
    const currentRoute = routesConfig.find(
      (route) => matchRoute(location.pathname, route.path) && route.protected
    );

    // If route is not protected, allow access
    if (!currentRoute?.protected) {
      setHasCheckedRoute(true);
      return;
    }

    // If no access token, redirect to auth
    if (!accessToken) {
      if (location.pathname !== "/auth") {
        navigate("/auth");
      }
      setHasCheckedRoute(true);
      return;
    }

    // If we have an access token but no user data, something is wrong
    if (!user) {
      httpService.clearAllData(); // Clear invalid data
      navigate("/auth");
      setHasCheckedRoute(true);
      return;
    }

    // If route is not protected, allow access
    if (
      ["/settings", "/user-activity", "/support"].includes(location.pathname) &&
      accessToken &&
      user
    ) {
      setHasCheckedRoute(true);
      return;
    }

    const permittedRoutes = getPermittedRoutes(user);

    // Check route permissions
    let submenuRoute: RouteConfig | undefined = undefined;
    if (currentRoute?.submenu) {
      submenuRoute = currentRoute.submenu.find((route) =>
        matchRoute(location.pathname, route.path)
      );
    }

    // const allowedRoles =
    //   currentRoute?.allowedRoles || submenuRoute?.allowedRoles;
    const permission =
      currentRoute?.permission || submenuRoute?.permission || "";

    if (!currentRoute && !submenuRoute) {
      navigate("/404");
      setHasCheckedRoute(true);
      return;
    }

    const isUserRoleAllowed = permission
      ? user.user_permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS ||
        (user.user_permissions as IPermissionCategories).modules?.some(
          (p) => p.codename === permission
        )
      : false;

    if (!hasCheckedRoute) {
      setHasCheckedRoute(true);
      if (!isUserRoleAllowed) {
        const firstPath = (permittedRoutes[0] || {})?.path || "/404";
        if (location.pathname !== firstPath) {
          navigate(firstPath);
        }
      }
    }
  }, [user, isLoading, hasCheckedRoute, navigate, location.pathname]);

  if (isLoading || !hasCheckedRoute) {
    return <Loading />;
  }

  return <Outlet />;
};

export default RouteInspector;
